import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth()+n, 1);

export default function AdminCalendar() {
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    const from = fmt(cursor);
    const to = fmt(new Date(cursor.getFullYear(), cursor.getMonth()+1, 0));
    axios.get(`${API}/admin/bookings`, { params: { date_from: from, date_to: to }, withCredentials: true })
      .then((r) => setBookings(r.data || []));
  }, [cursor]);

  const cells = useMemo(() => {
    const first = startOfMonth(cursor);
    const start = new Date(first);
    // Monday as first day of week
    const dow = (first.getDay() + 6) % 7;
    start.setDate(first.getDate() - dow);
    const arr = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [cursor]);

  const byDate = useMemo(() => {
    const m = {};
    for (const b of bookings) {
      (m[b.event_date] ||= []).push(b);
    }
    return m;
  }, [bookings]);

  return (
    <div data-testid="admin-calendar-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl text-[var(--ink)]">Naptár</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setCursor(addMonths(cursor, -1))} className="p-2 rounded-full border border-[rgba(44,31,26,0.18)] hover:border-[var(--burgundy)]"><ChevronLeft size={16} /></button>
          <span className="font-serif text-xl px-2 min-w-[180px] text-center">{cursor.toLocaleString("hu-HU", { month: "long", year: "numeric" })}</span>
          <button onClick={() => setCursor(addMonths(cursor, 1))} className="p-2 rounded-full border border-[rgba(44,31,26,0.18)] hover:border-[var(--burgundy)]"><ChevronRight size={16} /></button>
        </div>
      </div>
      <div className="tk-card p-4 md:p-5">
        <div className="grid grid-cols-7 text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-2">
          {["Hét","Ke","Sze","Csü","Pé","Szo","Vas"].map((d) => <div key={d} className="px-2 py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {cells.map((d, i) => {
            const inMonth = d.getMonth() === cursor.getMonth();
            const key = fmt(d);
            const items = byDate[key] || [];
            return (
              <div key={i} className={`min-h-[88px] rounded-xl border p-2 ${inMonth ? "bg-white border-[rgba(110,30,43,0.08)]" : "bg-[var(--beige-100)] border-transparent opacity-60"}`}>
                <div className="text-xs text-[var(--ink-soft)]">{d.getDate()}</div>
                <div className="mt-1 space-y-1">
                  {items.slice(0,3).map(b => (
                    <Link key={b.id} to={`/admin/bookings/${b.id}`} data-testid={`cal-booking-${b.id}`} className="block truncate text-[11px] px-1.5 py-1 rounded-md border border-[rgba(110,30,43,0.18)] bg-[rgba(110,30,43,0.06)] text-[var(--burgundy-dark)] hover:bg-[rgba(110,30,43,0.12)]">
                      <span className="inline-block w-2 h-2 rounded-full mr-1 align-middle" style={{ background: b.device?.color || "#6e1e2b" }} />
                      {b.customer_name}
                    </Link>
                  ))}
                  {items.length > 3 && (
                    <div className="text-[10px] text-[var(--ink-soft)]">+{items.length - 3} további</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
